import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Link, useParams } from 'react-router-dom';
import { FarkleGame, FarkleSnapshot } from '../games/farkle/logic';

const DEFAULT_PLAYERS = ['Host', 'Crew', 'FrogBot'];
const safeOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://qroke-a6fed.web.app';

const FarklePlaytestPage: React.FC = () => {
  const { partyId } = useParams();
  const joinUrl = partyId ? `${safeOrigin}/party/${partyId}/game/farkle/join` : `${safeOrigin}/dashboard`;
  const [playerSeed, setPlayerSeed] = useState(DEFAULT_PLAYERS.join(', '));
  const gameRef = useRef<FarkleGame>(new FarkleGame(DEFAULT_PLAYERS));
  const [snapshot, setSnapshot] = useState<FarkleSnapshot>(() => gameRef.current.serialize());
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [status, setStatus] = useState('Welcome to Frog Lab — roll whenever you are ready.');
  const [error, setError] = useState<string | null>(null);
  const [keptLog, setKeptLog] = useState<{ dice: number[]; points: number }[]>([]);
  const [showFarkleOverlay, setShowFarkleOverlay] = useState(false);
  const [farkleDice, setFarkleDice] = useState<number[]>([]);
  const [farkleLock, setFarkleLock] = useState(false);
  const farkleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTurnRef = useRef(snapshot.turn);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const currentPlayer = gameRef.current.getCurrentPlayer();
  const currentFarkleCount = snapshot.stats.farkleCounts[currentPlayer] ?? 0;
  const currentFarkleStreak = snapshot.stats.farkleStreaks[currentPlayer] ?? 0;

  const refreshSnapshot = useCallback(() => {
    setSnapshot(gameRef.current.serialize());
  }, []);

  // Procedurally synthesize a short sad-trombone sting so no asset file is required.
  const playSadTrombone = useCallback(() => {
    if (typeof window === 'undefined') return;
    const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContextClass();
    }
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
    const duration = 1.8;
    const now = ctx.currentTime;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(440, now);
    oscillator.frequency.exponentialRampToValueAtTime(120, now + duration);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.45, now + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }, []);

  const triggerFarkleSequence = useCallback(
    (diceValues: number[]) => {
      setFarkleDice(diceValues);
      setShowFarkleOverlay(true);
      setFarkleLock(true);
      setStatus('💀 FARKLE! No scoring dice — sad trombone incoming.');
      setError(null);
      setSelectedIndices([]);
      playSadTrombone();
      if (farkleTimeoutRef.current) {
        clearTimeout(farkleTimeoutRef.current);
      }
      farkleTimeoutRef.current = setTimeout(() => {
        setShowFarkleOverlay(false);
        setFarkleLock(false);
        setFarkleDice([]);
        setStatus('Farkle resolved. Next frog, take the stage.');
        refreshSnapshot();
      }, 2400);
    },
    [playSadTrombone, refreshSnapshot]
  );

  const resetGame = () => {
    const players = playerSeed
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);
    try {
      gameRef.current = new FarkleGame(players.length ? players : DEFAULT_PLAYERS);
      setSelectedIndices([]);
      setStatus('Fresh dice on the lily pad. Roll to begin.');
      setError(null);
      setKeptLog([]);
      lastTurnRef.current = 0;
      refreshSnapshot();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggleDiceIndex = (idx: number) => {
    if (!snapshot.rolledDice.length || farkleLock) return;
    setSelectedIndices((prev) => {
      if (prev.includes(idx)) {
        return prev.filter((value) => value !== idx);
      }
      return [...prev, idx].sort((a, b) => a - b);
    });
  };

  const keepSelectedDice = useCallback(
    ({ warnIfEmpty = true, quiet = false }: { warnIfEmpty?: boolean; quiet?: boolean } = {}) => {
      if (farkleLock) return false;
      if (!selectedIndices.length) {
        if (warnIfEmpty) {
          setStatus('Select scoring dice to keep first.');
        }
        return false;
      }
      const rolled = gameRef.current.rolledDice;
      const diceValues: number[] = [];
      for (const idx of selectedIndices) {
        const value = rolled[idx];
        if (typeof value !== 'number') {
          return false;
        }
        diceValues.push(value);
      }
      const points = gameRef.current.scoreRoll(diceValues);
      if (points === 0) {
        setStatus('Those dice do not score. Try a different combo.');
        return false;
      }
      const result = gameRef.current.selectDice(selectedIndices);
      if ('error' in result) {
        setError(result.error);
        return false;
      }
      setKeptLog((prev) => [...prev, { dice: diceValues, points }]);
      setSelectedIndices([]);
      refreshSnapshot();
      if (!quiet) {
        setStatus(result.hotDice ? '🔥 Hot dice! Everything resets — roll again.' : 'Dice locked. Roll again or bank.');
      }
      setError(null);
      return true;
    },
    [selectedIndices, refreshSnapshot, farkleLock]
  );

  const handleRoll = () => {
    if (farkleLock) {
      setStatus('One sec — finishing that Farkle moment.');
      return;
    }
    if (selectedIndices.length) {
      const locked = keepSelectedDice({ warnIfEmpty: false, quiet: true });
      if (!locked) {
        return;
      }
    } else if (gameRef.current.mustPick && gameRef.current.rolledDice.length > 0) {
      setStatus('Pick at least one scoring die before rolling again.');
      return;
    }
    const dice = gameRef.current.roll();
    setSelectedIndices([]);
    refreshSnapshot();
    setStatus(`Rolled ${dice.join(', ')} — tap scoring dice, keep them, or bank.`);
    setError(null);
    if (gameRef.current.farkleCheck()) {
      triggerFarkleSequence(dice);
    }
  };

  const handleKeepDice = () => {
    keepSelectedDice();
  };

  const handleBank = () => {
    if (farkleLock) return;
    gameRef.current.bank();
    setSelectedIndices([]);
    refreshSnapshot();
    setStatus('Score banked. Next frog in line.');
    setError(null);
  };

  const handleClearSelection = () => {
    if (farkleLock) return;
    setSelectedIndices([]);
    setError(null);
  };

  useEffect(() => {
    return () => {
      if (farkleTimeoutRef.current) {
        clearTimeout(farkleTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (lastTurnRef.current !== snapshot.turn) {
      lastTurnRef.current = snapshot.turn;
      setKeptLog([]);
    }
  }, [snapshot.turn]);

  useEffect(() => {
    if (farkleLock) return;
    if (!selectedIndices.length) return;
    const rolled = gameRef.current.rolledDice;
    if (!rolled.length) return;
    const values: number[] = [];
    for (const idx of selectedIndices) {
      const value = rolled[idx];
      if (typeof value !== 'number') {
        return;
      }
      values.push(value);
    }
    const selectionScore = gameRef.current.scoreRoll(values);
    if (selectionScore > 0) {
      const projected = gameRef.current.tentativeScore + selectionScore;
      setStatus(
        `Selected ${values.join(', ')} — worth ${selectionScore.toLocaleString()} pts (tentative ${projected.toLocaleString()} pts).`
      );
      setError(null);
    } else {
      setStatus('Those dice are not scoring yet — adjust your selection.');
    }
  }, [selectedIndices, farkleLock, snapshot.tentativeScore]);

  const lilyCard = (title: string, description: string) => (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-4">
      <p className="text-sm font-semibold text-emerald-200">{title}</p>
      <p className="mt-2 text-xs text-emerald-100/80">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-slate-950 to-slate-900 text-white">
      {showFarkleOverlay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-2xl">
          <div className="text-center">
            <p className="text-6xl font-black uppercase tracking-[0.6em] text-rose-200 drop-shadow-[0_0_35px_rgba(248,113,113,0.75)] animate-pulse">
              FARKLE
            </p>
            <p className="mt-4 text-lg font-semibold text-rose-100">Sad trombone… no scoring dice that roll.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {farkleDice.map((value, idx) => (
                <div
                  key={`farkle-die-${value}-${idx}`}
                  className="flex h-20 w-20 items-center justify-center rounded-3xl border-2 border-rose-400/80 bg-rose-900/40 text-4xl font-bold text-rose-100 shadow-2xl shadow-rose-900/70"
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10">
        <header className="flex flex-col gap-3 rounded-3xl border border-emerald-500/30 bg-emerald-950/60 p-6 shadow-2xl shadow-emerald-900/40 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-emerald-300">Frog Lab</p>
            <h1 className="text-3xl font-semibold">Farkle Playtest</h1>
            <p className="text-sm text-emerald-100/80">{partyId ? `Party ${partyId}` : 'Standalone sandbox'}</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-emerald-100/80">
            <span className="rounded-full bg-emerald-900/70 px-4 py-2">🐸 Frog mode engaged</span>
            <Link
              to="/dashboard"
              className="rounded-full border border-emerald-400/60 px-4 py-2 text-emerald-200 hover:bg-emerald-500/10"
            >
              ← Exit to Dashboard
            </Link>
          </div>
        </header>

        <section className="grid gap-6 lg:grid-cols-[3fr,2fr]">
          <div className="rounded-3xl border border-emerald-600/20 bg-slate-950/70 p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Current player</p>
                <p className="text-2xl font-semibold">{currentPlayer}</p>
                <p className="text-sm text-emerald-100/80">Tentative {snapshot.tentativeScore.toLocaleString()} pts • {snapshot.currentDice} dice active</p>
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <label className="text-emerald-100/80">
                  Players
                  <input
                    value={playerSeed}
                    onChange={(event) => setPlayerSeed(event.target.value)}
                    className="mt-1 w-full rounded-xl border border-emerald-500/40 bg-transparent px-3 py-2 text-sm focus:border-emerald-300 focus:outline-none"
                    placeholder="Comma separated names"
                  />
                </label>
                <button
                  onClick={resetGame}
                  className="rounded-xl bg-emerald-500/90 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
                >
                  Reset Game
                </button>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleRoll}
                  className="rounded-2xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
                >
                  🎲 Roll Dice
                </button>
                <button
                  onClick={handleKeepDice}
                  className="rounded-2xl border border-emerald-400 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-400/10"
                >
                  ♻ Keep Selected
                </button>
                <button
                  onClick={handleBank}
                  className="rounded-2xl border border-emerald-400/70 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-400/10"
                >
                  💼 Bank Score
                </button>
                <button
                  onClick={handleClearSelection}
                  className="rounded-2xl border border-emerald-400/40 px-4 py-2 text-sm text-emerald-100/80 hover:bg-emerald-400/5"
                >
                  Reset Selection
                </button>
              </div>

              <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/70 to-slate-900/60 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Dice Tray</p>
                  <span className="text-[0.65rem] uppercase tracking-[0.3em] text-emerald-200/80">{snapshot.currentDice} dice</span>
                </div>
                {snapshot.rolledDice.length === 0 ? (
                  <div className="mt-3 rounded-2xl border border-dashed border-emerald-400/50 px-4 py-8 text-center text-sm text-emerald-200/80">
                    Tap Roll to toss some frog-friendly dice.
                  </div>
                ) : (
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {snapshot.rolledDice.map((value, idx) => {
                      const active = selectedIndices.includes(idx);
                      return (
                        <button
                          key={`${value}-${idx}`}
                          onClick={() => toggleDiceIndex(idx)}
                          className={`rounded-2xl border px-4 py-3 text-center transition ${
                            active
                              ? 'border-lime-300/90 bg-emerald-500/20 text-white shadow-lg shadow-emerald-900'
                              : 'border-emerald-500/30 bg-slate-900/70 text-emerald-100/80 hover:border-emerald-300/70'
                          }`}
                        >
                          <div className="text-3xl">🎲</div>
                          <div className="text-2xl font-semibold">{value}</div>
                          <p className="text-xs text-emerald-200/70">{active ? 'Selected' : 'Tap to keep'}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-slate-900/60 to-emerald-900/40 p-4">
                <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Status</p>
                <p className="mt-2 text-lg font-semibold text-emerald-100">{status}</p>
                {error && <p className="mt-2 rounded-lg bg-rose-900/40 px-3 py-2 text-sm text-rose-200">{error}</p>}
              </div>
              <div className="rounded-2xl border border-emerald-500/30 bg-slate-950/70 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Farkle Counter</p>
                  <span className="text-[0.65rem] uppercase tracking-[0.3em] text-emerald-200/80">3 strikes = -1k</span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/40 px-3 py-2 text-center">
                    <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/70">Streak</p>
                    <p className={`text-2xl font-bold ${currentFarkleStreak >= 2 ? 'text-rose-300' : 'text-emerald-100'}`}>{currentFarkleStreak}</p>
                  </div>
                  <div className="rounded-xl border border-emerald-500/20 bg-emerald-900/40 px-3 py-2 text-center">
                    <p className="text-xs uppercase tracking-[0.2em] text-emerald-200/70">Total</p>
                    <p className="text-2xl font-bold text-emerald-100">{currentFarkleCount}</p>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="h-2 w-full rounded-full bg-emerald-900/40">
                    <div
                      className={`h-full rounded-full ${
                        currentFarkleStreak === 0
                          ? 'bg-emerald-500'
                          : currentFarkleStreak === 1
                          ? 'bg-amber-400'
                          : 'bg-rose-500'
                      }`}
                      style={{ width: `${Math.min((currentFarkleStreak / 3) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-emerald-200/80">
                    {currentFarkleStreak >= 3
                      ? 'Penalty applied! Score drops by 1,000 — streak resets next roll.'
                      : currentFarkleStreak === 2
                      ? 'Careful — one more Farkle costs you 1,000 points.'
                      : 'Stay steady. Three Farkles in a row trigger the -1,000 penalty.'}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Kept Dice Log</p>
                  {keptLog.length > 0 && <span className="text-[0.65rem] uppercase tracking-[0.2em] text-emerald-200/70">{keptLog.length} keeps</span>}
                </div>
                {keptLog.length === 0 ? (
                  <p className="mt-3 text-sm text-emerald-100/70">No scoring dice locked yet — tap Keep to start flexing.</p>
                ) : (
                  <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
                    {keptLog.map((entry, idx) => (
                      <div
                        key={`kept-entry-${idx}`}
                        className="flex items-center justify-between rounded-xl border border-emerald-500/20 bg-emerald-900/30 px-3 py-2"
                      >
                        <div className="flex flex-wrap gap-1 text-sm text-emerald-100">
                          {entry.dice.map((value, dieIdx) => (
                            <span
                              key={`kept-die-${idx}-${dieIdx}`}
                              className="rounded-lg bg-emerald-800/70 px-2 py-1 text-base font-semibold text-emerald-50"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm font-bold text-lime-300">+{entry.points.toLocaleString()} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-3xl border border-emerald-600/30 bg-emerald-950/50 p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">QR Sign-In</p>
              {partyId ? (
                <div className="mt-4 flex flex-col items-center gap-4 text-center text-sm text-emerald-100/80">
                  <div className="rounded-3xl bg-white p-4">
                    <QRCode value={joinUrl} size={140} />
                  </div>
                  <p className="font-semibold">Players scan this to jump into the queue.</p>
                  <p className="text-xs text-emerald-200/80">{joinUrl}</p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-emerald-100/70">
                  Launch this playtest from a party link to auto-generate a QR join code for the crowd.
                </p>
              )}
            </div>
            <div className="rounded-3xl border border-emerald-600/30 bg-emerald-950/40 p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Scoreboard</p>
              <div className="mt-4 space-y-3">
                {snapshot.players.map((player) => (
                  <div key={player} className="flex items-center justify-between rounded-2xl bg-emerald-900/30 px-4 py-3">
                    <div>
                      <p className="text-lg font-semibold">{player}</p>
                      <p className="text-xs text-emerald-200/80">{player === currentPlayer ? 'Taking a turn' : 'Waiting'}</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-200">{(snapshot.scores[player] ?? 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-2xl border border-emerald-400/30 bg-slate-950/70 px-4 py-3 text-sm text-emerald-100/80">
                {snapshot.hotDice ? '🔥 Hot dice unlocked — you get all six back.' : snapshot.mustPick ? 'Select a scoring die before rolling again.' : 'Keep pressing roll or bank to chase 10K.'}
              </div>
            </div>

            <div className="rounded-3xl border border-emerald-600/20 bg-slate-950/70 p-6">
              <p className="text-xs uppercase tracking-[0.3em] text-emerald-300">Lily Pad Notes</p>
              <div className="mt-4 grid gap-3 text-sm">
                {lilyCard('Subtle frog glow', 'Gradient edges keep the playtest aligned with the QrOke palette while signaling experiment mode.')}
                {lilyCard('Computer dice option', 'This sandbox runs entirely in-browser, so you can iterate without touching Firestore during tests.')}
                {lilyCard('Backtrack friendly', 'Use the back link above to drop straight into host controls when you are finished exploring.')}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FarklePlaytestPage;
